import type { LessonStatusReasons } from '@composables/widgets/lessons/lesson-status'

import { useLessonEditStore } from '@stores/lesson-edit'
import { useListsStore } from '@stores/lists'

import { useLessonShowApi } from '@api/lessons/show.hook'

interface LessonContextOptions {
  /** Reread the lesson even when it is loaded: arriving from another page, or after a write. */
  force?: boolean
}

interface LessonContextReturn {
  refIsLoading: ComputedRef<boolean>
  loadLesson: (disciplineId: string, lessonId: string, options?: LessonContextOptions) => Promise<void>
}

/** Loads run one after another, so the answer for a lesson left behind can never land over the next one. */
let queue: Promise<void> = Promise.resolve()

/** The lesson every tab of the lesson page shows, read once and kept while the admin moves between its tabs. */
export const useLessonContext = (): LessonContextReturn => {
  const store = useLessonEditStore()
  const listsStore = useListsStore()

  const refIsLoading = computed<boolean>(() => store.isLoading)

  const read = async (disciplineId: string, lessonId: string): Promise<void> => {
    const [data, lists] = await Promise.all([
      useLessonShowApi({ disciplineId, lessonId }),
      listsStore.ensure(),
    ])

    store.setLesson(data?.lesson ?? null)
    store.setSlots(data?.slots ?? null)

    if (data?.lesson) {
      const reasons = (lists?.booking.change_status_reasons ?? {}) as LessonStatusReasons
      store.initForm(data.lesson, reasons)
    }
  }

  const loadLesson = (disciplineId: string, lessonId: string, options: LessonContextOptions = {}): Promise<void> => {
    const isLoaded = String(store.getLesson?.id) === lessonId && String(store.getLesson?.discipline_id) === disciplineId
    if (isLoaded && !options.force) return Promise.resolve()

    if (!isLoaded) store.reset()
    store.setLoading(true)

    const promise = queue.then(() => read(disciplineId, lessonId)).catch(() => undefined)
    queue = promise

    void promise.finally(() => {
      if (queue === promise) store.setLoading(false)
    })

    return promise
  }

  return {
    refIsLoading,
    loadLesson,
  }
}
